<template>
  <div id="app" class="homepage">
    <!-- 骨架屏 -->
    <div class="skeleton-screen" v-if="isLoading">
      <div class="skeleton-header">
        <div class="skeleton-logo"></div>
        <div class="skeleton-nav">
          <div class="skeleton-item"></div>
          <div class="skeleton-item"></div>
          <div class="skeleton-item"></div>
        </div>
      </div>
      <div class="skeleton-banner">
        <div class="skeleton-title"></div>
        <div class="skeleton-subtitle"></div>
        <div class="skeleton-btn"></div>
      </div>
      <div class="skeleton-content">
        <div class="skeleton-card"></div>
        <div class="skeleton-card"></div>
        <div class="skeleton-card"></div>
      </div>
    </div>
    
    <!-- 实际内容 -->
    <template v-else>
      <!-- 头部覆盖在内容上 -->
      <AppHeader />
      
      <!-- 主内容区域 -->
      <main class="main-content">
        <RouterView v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </RouterView>
      </main>
      
      <AppFooter />
      
      <!-- 回到顶部 -->
      <div class="back-to-top" v-show="showBackTop" @click="scrollToTop">
        <i class="iconfont icon-top"></i>
      </div>
    </template>
    
    <!-- 语言切换弹窗 -->
    <div class="lang-modal" v-if="showLangModal">
      <div class="lang-modal-overlay" @click="dismissLangModal"></div>
      <div class="lang-modal-content">
        <div class="lang-modal-icon">
          <img src="https://assets.byteplus.com/obj/byteplus-assets/navigation/icon-language-white.svg" width="32" height="32" alt="language" style="filter: invert(30%) sepia(98%) saturate(1640%) hue-rotate(203deg) brightness(101%) contrast(105%);" />
        </div>
        <h3 class="lang-modal-title">{{ suggestedLang === 'zh' ? '切换到中文？' : 'Switch to English?' }}</h3>
        <p class="lang-modal-desc">{{ suggestedLang === 'zh' ? '检测到您的浏览器语言为中文' : 'Your browser language is English' }}</p>
        <div class="lang-modal-buttons">
          <button class="btn-cancel" @click="dismissLangModal">{{ suggestedLang === 'zh' ? '保持 English' : 'Keep 中文' }}</button>
          <button class="btn-confirm" @click="switchLanguage">{{ suggestedLang === 'zh' ? '切换到中文' : 'Switch to English' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup name="App">
import { ref, onMounted, onUnmounted } from 'vue'
import { useHead } from '@vueuse/head'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const showBackTop = ref(false)
const showLangModal = ref(false)
const suggestedLang = ref('en')
const isLoading = ref(true)
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleScroll = () => {
  showBackTop.value = window.scrollY > 200
}

// 检测浏览器语言
const checkBrowserLanguage = () => {
  const hasAsked = localStorage.getItem('lang_asked')
  if (hasAsked) return
  
  const browserLang = navigator.language || navigator.userLanguage
  const currentLang = localStorage.getItem('lang') || 'en'
  
  // 浏览器是中文但当前是英文
  if (browserLang.startsWith('zh') && currentLang === 'en') {
    suggestedLang.value = 'zh'
    showLangModal.value = true
  }
  // 浏览器是英文但当前是中文
  else if (browserLang.startsWith('en') && currentLang === 'zh') {
    suggestedLang.value = 'en'
    showLangModal.value = true
  }
}

const switchLanguage = () => {
  localStorage.setItem('lang', suggestedLang.value)
  localStorage.setItem('lang_asked', 'true')
  showLangModal.value = false
  window.dispatchEvent(new CustomEvent('language-change', { detail: suggestedLang.value }))
}

const dismissLangModal = () => {
  localStorage.setItem('lang_asked', 'true')
  showLangModal.value = false
}

// SEO默认配置
useHead({
  titleTemplate: (title) => title ? `${title} - 95008星云全球` : '95008 - 星云全球云服务平台',
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0'
    }
  ]
})

onMounted(() => {
  console.log('App.vue mounted')
  document.documentElement.setAttribute('data-theme', 'light')
  window.addEventListener('scroll', handleScroll)
  // 骨架屏显示时间 - 在生产环境下立即关闭
  const delay = import.meta.env.PROD ? 0 : 800
  setTimeout(() => {
    isLoading.value = false
    console.log('Loading screen hidden, main content should be visible')
    // 确认Footer是否渲染
    setTimeout(() => {
      const footer = document.querySelector('footer')
      console.log('Footer found:', !!footer)
      if (!footer) {
        console.error('Footer not rendered!')
      }
    }, 100)
  }, delay)
  // 首次访问检测语言
  setTimeout(checkBrowserLanguage, 1500)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 语言切换弹窗
.lang-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lang-modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
}
.lang-modal-content {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.lang-modal-icon {
  margin-bottom: 16px;
}
.lang-modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
}
.lang-modal-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
}
.lang-modal-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.lang-modal-buttons button {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-cancel {
  background: #f5f5f5;
  border: none;
  color: #666;
}
.btn-cancel:hover {
  background: #e8e8e8;
}
.btn-confirm {
  background: #006eff;
  border: none;
  color: #fff;
}
.btn-confirm:hover {
  background: #0052cc;
}

// 骨架屏样式
.skeleton-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0d1117;
  z-index: 9999;
}
.skeleton-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 40px;
  background: rgba(255,255,255,0.03);
}
.skeleton-logo {
  width: 120px;
  height: 32px;
  background: linear-gradient(90deg, #1a1f26 25%, #242a33 50%, #1a1f26 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}
.skeleton-nav {
  display: flex;
  gap: 24px;
}
.skeleton-item {
  width: 80px;
  height: 20px;
  background: linear-gradient(90deg, #1a1f26 25%, #242a33 50%, #1a1f26 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}
.skeleton-banner {
  padding: 120px 40px 80px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.skeleton-title {
  width: 400px;
  height: 48px;
  background: linear-gradient(90deg, #1a1f26 25%, #242a33 50%, #1a1f26 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}
.skeleton-subtitle {
  width: 300px;
  height: 24px;
  background: linear-gradient(90deg, #1a1f26 25%, #242a33 50%, #1a1f26 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}
.skeleton-btn {
  width: 140px;
  height: 44px;
  background: linear-gradient(90deg, #1a1f26 25%, #242a33 50%, #1a1f26 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 22px;
  margin-top: 16px;
}
.skeleton-content {
  padding: 40px;
  display: flex;
  gap: 24px;
}
.skeleton-card {
  flex: 1;
  height: 200px;
  background: linear-gradient(90deg, #1a1f26 25%, #242a33 50%, #1a1f26 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
</style>